import { createFileRoute, useParams } from '@tanstack/react-router';
import { HStack, VStack } from '@/components/Layout';
import { AddStepperCompleteDialog } from '@/components/Stepper';
import Processing from '@/features/import-csv/-components/Processing';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { LIST_EMPTY_DATA } from '@/lib/listPage';
import { useGetCategoryFieldAccessPermissions } from '@/routes/_staff/settings/permission-and-roles/-query/category-field-access-hooks';
import AddStepperNavigation from '../../-components/AddStepperNavigation';
import AddStepperPanelProgress from '../../-components/AddStepperPanelProgress';
import AddStepperStepTitleCard from '../../-components/AddStepperStepTitleCard';
import BasicForm from '../../-components/BasicForm';
import SelectCategoryPermission from '../../-components/SelectCategoryPermission';
import SelectPeopleGroup from '../../-components/SelectPeopleGroup';
import SelectPermission from '../../-components/SelectPermission';
import { ROLE_CREATE_HASH } from '../../-constants';
import useBasicForm from '../../-hooks/useBasicForm';
import useHash from '../../-hooks/useHash';
import useSelectCategoryPermission from '../../-hooks/useSelectCategoryPermission';
import useSelectPeopleGroup from '../../-hooks/useSelectPeopleGroup';
import useSelectPermission from '../../-hooks/useSelectPermission';
import useUpdateRole from '../../-mutations/useUpdateRole';
import useGetRoleDetail from '../../../../_detail/roles/$id/-query/useGetRoleDetail';
import useGetPeopleGroups from '../../../../_list/people-groups/-queries/useGetPeopleGroups';
import useGetProductPermissions from '../../../../_list/product-permissions/-queries/useGetProductPermissions';
import { TabKey } from '../../../../_list/roles/-constants';
import useGetRoles from '../../../../_list/roles/-queries/useGetRoles';
export const Route = createFileRoute('/_staff/settings/permission-and-roles/_form/roles/$id/edit/')({
  component: RouteComponent
});
function RouteComponent() {
  const {
    id: roleId
  } = useParams({
    from: '/_staff/settings/permission-and-roles/_form/roles/$id/edit/'
  });
  const BASE_LINK = `/settings/permission-and-roles/roles/${roleId}/edit`;
  const {
    data
  } = useGetRoleDetail({
    roleId
  });
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.settings.permissionAndRoles.createRole'
  });
  const {
    hash
  } = useHash();
  const {
    data: rolesRes
  } = useGetRoles({
    search: {
      category: TabKey.ALL,
      pageIndex: 0,
      pageSize: 100
    }
  });
  const {
    basicFormState,
    handleChange,
    isDuplicateName
  } = useBasicForm({
    data,
    existingRoles: rolesRes ?? LIST_EMPTY_DATA
  });
  const {
    selectPeopleGroupState,
    handleChangeOptions: handleChangeOptionsPeopleGroup,
    handleChangeSearchText: handleChangeSearchTextPeopleGroup,
    handleClearAll: handleClearAllPeopleGroup
  } = useSelectPeopleGroup({
    data
  });
  const {
    selectPermissionState,
    handleChangeOptions: handleChangeOptionsPermission,
    handleChangeSearchText: handleChangeSearchTextPermission,
    handleClearAll: handleClearAllPermission
  } = useSelectPermission({
    data
  });

  // for select people group
  const {
    data: peopleGroupsRes
  } = useGetPeopleGroups({
    search: {
      category: TabKey.ALL,
      pageIndex: 0,
      pageSize: 10,
      freeText: selectPeopleGroupState.searchText
    }
  });

  // for select permission
  const {
    data: productPermissionsRes
  } = useGetProductPermissions({
    search: {
      category: TabKey.ALL,
      pageIndex: 0,
      pageSize: 10,
      freeText: selectPermissionState.searchText
    }
  });
  const {
    mutate: createRole,
    isPending,
    isComplete
  } = useUpdateRole({
    roleId
  });
  const {
    selectCategoryPermissionState,
    handleChangeOptions: handleChangeOptionsCategoryPermission,
    handleChangeSearchText: handleChangeSearchTextCategoryPermission,
    handleClearAll: handleClearAllCategoryPermission
  } = useSelectCategoryPermission({
    data
  });
  const {
    data: categoryPermissionsRes
  } = useGetCategoryFieldAccessPermissions({
    freeText: selectCategoryPermissionState.searchText || undefined,
    pageSize: 10
  });
  return <HStack className="gap-9 p-10 h-[calc(100vh-64px)]">
      <AddStepperNavigation baseLink={BASE_LINK} currentHash={hash} isEdit={true} />

      <VStack className="gap-6 w-full">
        {isPending && <Processing hash={hash} />}

        {!isPending && <AddStepperStepTitleCard hash={hash} />}

        {!isPending && hash === ROLE_CREATE_HASH.FORM && <BasicForm basicFormState={basicFormState} handleChange={handleChange} isDuplicateName={isDuplicateName} />}

        {!isPending && hash === ROLE_CREATE_HASH.SELECT_PEOPLE_GROUP && <SelectPeopleGroup selectPeopleGroupState={selectPeopleGroupState} handleChangeOptions={handleChangeOptionsPeopleGroup} handleChangeSearchText={handleChangeSearchTextPeopleGroup} handleClearAll={handleClearAllPeopleGroup} peopleGroups={peopleGroupsRes?.items ?? []} />}

        {!isPending && hash === ROLE_CREATE_HASH.SET_PERMISSION && <SelectPermission selectPermissionState={selectPermissionState} handleChangeOptions={handleChangeOptionsPermission} handleChangeSearchText={handleChangeSearchTextPermission} handleClearAll={handleClearAllPermission} permissions={productPermissionsRes?.items ?? []} />}

        {!isPending && hash === ROLE_CREATE_HASH.SELECT_CATEGORY_PERMISSION && <SelectCategoryPermission selectCategoryPermissionState={selectCategoryPermissionState} handleChangeOptions={handleChangeOptionsCategoryPermission} handleChangeSearchText={handleChangeSearchTextCategoryPermission} handleClearAll={handleClearAllCategoryPermission} categoryPermissions={categoryPermissionsRes?.items ?? []} />}

        {!isPending && <AddStepperPanelProgress baseLink={BASE_LINK} currentHash={hash} basicFormState={basicFormState} selectPeopleGroupState={selectPeopleGroupState} selectPermissionState={selectPermissionState} selectCategoryPermissionState={selectCategoryPermissionState} mutate={createRole} isDuplicateName={isDuplicateName} />}

        {isComplete && <AddStepperCompleteDialog completeMessage={t('addStepperCompleteDialog.update.completeMessage')} completeLinkButtonText={t('addStepperCompleteDialog.update.completeLinkButtonText')} completeLinkUrl="/settings/permission-and-roles/roles" />}
      </VStack>
    </HStack>;
}